import type {Icon} from '@primer/octicons-react'

import {MemexActionType, type MemexWorkflowAction, MemexWorkflowTriggerType} from '../../../api/workflows/contracts'
import {getWorkflowIcon} from '../../../helpers/workflow-utilities'
import {useAutomationGraph} from '../../../state-providers/workflows/use-automation-graph'
import {WorkflowResources} from '../../../strings'
import {Connector} from '../connector'
import {AddItemBlock} from './add-item-block'
import {ArchiveBlock} from './archive-block'
import {AssignCopilotBlock} from './assign-copilot-block'
import {CloseItemBlock} from './close-item-block'
import {GetItemsBlock} from './get-items-block'
import {GetProjectItemsBlock} from './get-project-items-block'
import {GetSubIssuesBlock} from './get-sub-issues-block'
import {SetBlock} from './set-block'
import {WhenBlock} from './when-block'

type ActionBlockProps = {
  action: MemexWorkflowAction
  onNoSuggestedRepositories?: () => void
}

type WhenBlockHeaderProps = {
  description: string
  icon: Icon
  iconColor: string
  iconBg: string
}

function getWhenBlockHeaderPropsByTriggerType(triggerType: MemexWorkflowTriggerType): WhenBlockHeaderProps {
  switch (triggerType) {
    case MemexWorkflowTriggerType.ItemAdded: {
      return {
        description: WorkflowResources.itemAddedToProjectLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-success)',
        iconBg: 'var(--bgColor-success-muted)',
      }
    }
    case MemexWorkflowTriggerType.Reopened: {
      return {
        description: WorkflowResources.itemReopenedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-success)',
        iconBg: 'var(--bgColor-success-muted)',
      }
    }
    case MemexWorkflowTriggerType.Closed: {
      return {
        description: WorkflowResources.itemClosedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-muted)',
        iconBg: 'var(--bgColor-inset)',
      }
    }
    case MemexWorkflowTriggerType.ReviewChangesRequested: {
      return {
        description: WorkflowResources.codeChangesRequestedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-danger)',
        iconBg: 'var(--bgColor-danger-muted)',
      }
    }
    case MemexWorkflowTriggerType.ReviewApproved: {
      return {
        description: WorkflowResources.codeReviewApprovedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-open)',
        iconBg: 'var(--bgColor-success-muted)',
      }
    }
    case MemexWorkflowTriggerType.Merged: {
      return {
        description: WorkflowResources.pullRequestMergedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-done)',
        iconBg: 'var(--bgColor-done-muted)',
      }
    }
    case MemexWorkflowTriggerType.PrConnected: {
      return {
        description: WorkflowResources.pullRequestConnectedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-open)',
        iconBg: 'var(--bgColor-open-muted)',
      }
    }
    case MemexWorkflowTriggerType.AutoAssignAgent: {
      return {
        description: WorkflowResources.autoAssignAgentStatusUpdatedLabel,
        icon: getWorkflowIcon(triggerType),
        iconColor: 'var(--fgColor-accent)',
        iconBg: 'var(--bgColor-accent-muted)',
      }
    }
    default: {
      throw new Error(`${triggerType} is not a valid MemexWorkflowTriggerType for a WhenBlock`)
    }
  }
}

export const ActionBlock = ({action, onNoSuggestedRepositories}: ActionBlockProps) => {
  const {workflow} = useAutomationGraph()
  switch (action.actionType) {
    case MemexActionType.SetField: {
      const whenBlockHeaderProps = getWhenBlockHeaderPropsByTriggerType(workflow.triggerType)
      return (
        <>
          <WhenBlock {...whenBlockHeaderProps} />
          <Connector />
          <SetBlock headerDescription={WorkflowResources.setValueLabel} />
        </>
      )
    }
    case MemexActionType.GetProjectItems: {
      if (workflow.triggerType === MemexWorkflowTriggerType.ProjectItemColumnUpdate) {
        return <SetBlock headerDescription={WorkflowResources.projectItemColumnUpdateLabel} />
      } else if (workflow.triggerType === MemexWorkflowTriggerType.AutoAssignAgent) {
        // Auto-assign-agent's source-column selection is rendered inside AssignCopilotBlock;
        // we don't surface a separate filter-items card.
        return null
      } else {
        return <GetProjectItemsBlock />
      }
    }
    case MemexActionType.GetItems: {
      return <GetItemsBlock onNoSuggestedRepositories={onNoSuggestedRepositories} />
    }
    case MemexActionType.ArchiveProjectItem: {
      return <ArchiveBlock />
    }
    case MemexActionType.AddProjectItem: {
      return (
        <AddItemBlock
          headerDescription={
            action.arguments.subIssue ? WorkflowResources.addSubIssuesBlockLabel : WorkflowResources.addItemBlockLabel
          }
        />
      )
    }
    case MemexActionType.CloseItem: {
      return <CloseItemBlock />
    }
    case MemexActionType.GetSubIssues: {
      return <GetSubIssuesBlock />
    }
    case MemexActionType.AssignCopilot: {
      // Auto-assign-agent has its own self-contained panel — no WhenBlock/Connector wrapper.
      return <AssignCopilotBlock />
    }
    default: {
      return null
    }
  }
}
